import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Stack,
  Typography,
  Container,
  Box,
  Chip,
  Divider,
  Skeleton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Tooltip,
  List,
  ListItem,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import dealsService, { Deal as DealType } from '../../api/services/deals';
import offersService, { OfferWithVehicle } from '../../api/services/offers';
import messagesService, { Message } from '../../api/services/messages';
import CarListingRow from '../Cars/components/CarListingRow';
import {
  formatUsd,
  IMPROVEMENT_LABELS,
  INCENTIVE_LABELS,
  scoreColor,
  scoreOffers,
} from '../../utils/vehicleComparison';
import DealSummary from './DealSummary';

const POLL_MS = 30000;

/** Matches backend/src/outreach/comps.ts CitedComp — what's persisted on offer.compsCited. */
interface CitedComp {
  dealer: string;
  city: string;
  state: string;
  dist: number;
  price: number;
  miles: number;
  year: number;
  trim: string;
  vdpUrl: string;
  msrp?: number;
  dealerDiscountPct?: number;
}

const compsCitedOf = (offer: OfferWithVehicle): CitedComp[] => {
  const raw = (offer as any).compsCited;
  return Array.isArray(raw) ? (raw as CitedComp[]) : [];
};

const Deal = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [deal, setDeal] = useState<DealType | null>(null);
  const [offers, setOffers] = useState<OfferWithVehicle[] | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{
    offer: OfferWithVehicle;
    action: 'ACCEPTED' | 'REJECTED';
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const offersRef = useRef<OfferWithVehicle[] | null>(null);
  offersRef.current = offers;

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [d, o, m] = await Promise.all([
        dealsService.get(id),
        offersService.find(id),
        messagesService.find(id).catch(() => []),
      ]);
      setDeal(d);
      setOffers(o);
      setMessages(m);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Unable to load this deal.');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while any dealer hasn't answered and the deal is still open.
  useEffect(() => {
    if (!deal || deal.status === 'ACCEPTED') return undefined;
    const timer = setInterval(() => {
      const current = offersRef.current;
      if (
        current?.some((o) => !o.price) &&
        new Date(deal.offerExpDate) > new Date()
      ) {
        load();
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [deal, load]);

  const scores = useMemo(
    () =>
      scoreOffers(
        (offers ?? [])
          .filter((o) => o.price && o.status !== 'REJECTED')
          .map((o) => ({
            id: o.id,
            price: o.price as number,
            mileage: o.vehicle.mileage,
            listPrice: o.vehicle.listPrice,
            improvements: o.improvements,
            incentives: o.incentives,
          }))
      ),
    [offers]
  );

  const messagesByOffer = useMemo(() => {
    const map = new Map<string, Message[]>();
    for (const m of messages) {
      if (!m.offerId) continue;
      const list = map.get(m.offerId) ?? [];
      list.push(m);
      map.set(m.offerId, list);
    }
    return map;
  }, [messages]);

  const sortedOffers = useMemo(() => {
    if (!offers) return [];
    const rankOf = (o: OfferWithVehicle) =>
      scores.get(o.id)?.rank ?? Number.MAX_SAFE_INTEGER;
    return [...offers].sort((a, b) => {
      if (a.status === 'ACCEPTED') return -1;
      if (b.status === 'ACCEPTED') return 1;
      return rankOf(a) - rankOf(b);
    });
  }, [offers, scores]);

  const decide = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await offersService.setStatus(confirm.offer.id, confirm.action);
      enqueueSnackbar(
        confirm.action === 'ACCEPTED'
          ? `Accepted. ${confirm.offer.dealer} now has your contact info and will reach out.`
          : `Declined ${confirm.offer.dealer}'s offer.`,
        { variant: confirm.action === 'ACCEPTED' ? 'success' : 'info' }
      );
      setConfirm(null);
      await load();
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.data?.message || 'Could not update this offer.',
        { variant: 'error' }
      );
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!deal || !offers) {
    return <Skeleton variant="rectangular" height={400} />;
  }

  const dealClosed = deal.status === 'ACCEPTED';
  const responded = offers.filter((o) => o.price).length;

  return (
    <Container maxWidth="lg">
      <DealSummary
        deal={deal}
        requested={offers.length}
        responded={responded}
      />
      <Stack spacing={2} sx={{ py: 4 }}>
        <Typography variant="h5">Offers</Typography>
        {offers.length === 0 && (
          <Typography variant="body1" color="text.secondary">
            No dealers were attached to this deal.
          </Typography>
        )}
        {sortedOffers.map((offer) => {
          const score = scores.get(offer.id);
          const accepted = offer.status === 'ACCEPTED';
          const rejected = offer.status === 'REJECTED';
          const accent = (theme: any) =>
            accepted
              ? theme.palette.success.main
              : score
              ? scoreColor(
                  score.score,
                  theme.palette.success.main,
                  theme.palette.error.main
                )
              : theme.palette.grey[300];
          return (
            <Card
              key={offer.id}
              sx={{
                p: 3,
                opacity: rejected ? 0.6 : 1,
                borderLeft: (theme) => `6px solid ${accent(theme)}`,
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'start', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box>
                    <Typography variant="h6">{offer.dealer}</Typography>
                    {accepted && (
                      <Chip size="small" color="success" label="Accepted" />
                    )}
                    {rejected && <Chip size="small" label="Declined" />}
                  </Box>
                  {offer.price && (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box textAlign="right">
                        <Typography variant="h5" fontWeight="bold">
                          {formatUsd(offer.price)}
                        </Typography>
                        {score?.savingsVsList !== undefined && (
                          <Typography
                            variant="caption"
                            color={
                              score.savingsVsList >= 0
                                ? 'success.main'
                                : 'error.main'
                            }
                          >
                            {score.savingsVsList >= 0 ? '−' : '+'}
                            {formatUsd(Math.abs(score.savingsVsList))} vs.
                            listed {formatUsd(offer.vehicle.listPrice)}
                          </Typography>
                        )}
                      </Box>
                      {score && (
                        <Tooltip
                          title={`Rank ${score.rank} of ${
                            scores.size
                          }. Effective cost ${formatUsd(
                            score.effectiveCost
                          )} = price + $0.15/mile − ${formatUsd(
                            score.addedValue
                          )} of included extras.`}
                        >
                          <Chip
                            label={`Score ${score.score}`}
                            sx={{
                              fontWeight: 'bold',
                              color: 'white',
                              backgroundColor: (theme) =>
                                scoreColor(
                                  score.score,
                                  theme.palette.success.main,
                                  theme.palette.error.main
                                ),
                            }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                </Stack>

                {(offer.incentives.length > 0 ||
                  offer.improvements.length > 0) && (
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {offer.improvements.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2">
                          Included reconditioning
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            mt: 0.5,
                          }}
                        >
                          {offer.improvements.map((imp) => (
                            <Chip
                              key={imp}
                              label={IMPROVEMENT_LABELS[imp]}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {offer.incentives.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2">
                          Service extras
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            mt: 0.5,
                          }}
                        >
                          {offer.incentives.map((inc) => (
                            <Chip
                              key={inc}
                              label={INCENTIVE_LABELS[inc]}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Stack>
                )}

                <Divider />
                <CarListingRow
                  car={{
                    id: offer.vehicleId,
                    vin: offer.vehicle.vin,
                    make: offer.vehicle.make,
                    model: offer.vehicle.model,
                    trim: offer.vehicle.trim,
                    version: offer.vehicle.version,
                    photos: offer.vehicle.photos,
                    year: offer.vehicle.year,
                    mileage: offer.vehicle.mileage,
                    listPrice: offer.vehicle.listPrice,
                    msrp: offer.vehicle.msrp,
                  }}
                  dealer={{ name: offer.dealer, city: '', state: '', dist: 0 }}
                  isSelected={false}
                  slim
                />

                {offer.otdPrice != null && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography variant="subtitle2">
                      Written OTD: {formatUsd(offer.otdPrice)}
                    </Typography>
                    {offer.docFee != null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Doc fee ${formatUsd(offer.docFee)}`}
                      />
                    )}
                    {offer.marketAdjustment != null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        label={`Market adjustment ${formatUsd(
                          offer.marketAdjustment
                        )}`}
                      />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={offer.itemized ? 'Itemized' : 'Lump sum'}
                    />
                  </Stack>
                )}

                {compsCitedOf(offer).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2">
                      Comps cited in your request
                    </Typography>
                    <List dense disablePadding sx={{ mt: 0.5 }}>
                      {compsCitedOf(offer).map((comp, i) => (
                        <ListItem
                          key={`${comp.vdpUrl}-${i}`}
                          disableGutters
                          sx={{ py: 0.25 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {comp.year} {comp.trim || 'trim n/a'} —{' '}
                            {comp.miles.toLocaleString()} miles —{' '}
                            {formatUsd(comp.price)}
                            {typeof comp.dealerDiscountPct === 'number' &&
                              ` (${Math.round(
                                comp.dealerDiscountPct * 100
                              )}% off MSRP)`}{' '}
                            — {Math.round(comp.dist)} mi away
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {(messagesByOffer.get(offer.id)?.length ?? 0) > 0 && (
                  <Box>
                    <Typography variant="subtitle2">Message thread</Typography>
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      {messagesByOffer.get(offer.id)!.map((m) => (
                        <Box
                          key={m.id}
                          sx={{
                            pl: 1,
                            borderLeft: '2px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {m.direction === 'OUT' ? 'You' : m.fromAddress} —{' '}
                            {new Date(
                              (m.direction === 'OUT'
                                ? m.sentAt
                                : m.receivedAt) ?? m.createdAt
                            ).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {m.subject}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {m.bodyText.slice(0, 300)}
                            {m.bodyText.length > 300 ? '…' : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {!offer.price ? (
                  <Stack direction="row" spacing={2} alignItems="center">
                    {rejected ? (
                      <Typography color="text.secondary">
                        This dealer did not respond in time.
                      </Typography>
                    ) : (
                      <>
                        <CircularProgress size={20} />
                        <Typography variant="body2">
                          Waiting for this dealer to respond.
                        </Typography>
                      </>
                    )}
                  </Stack>
                ) : (
                  !dealClosed &&
                  !rejected && (
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <Button
                        color="inherit"
                        onClick={() =>
                          setConfirm({ offer, action: 'REJECTED' })
                        }
                      >
                        Decline
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() =>
                          setConfirm({ offer, action: 'ACCEPTED' })
                        }
                      >
                        Accept offer
                      </Button>
                    </Stack>
                  )
                )}
              </Stack>
            </Card>
          );
        })}
      </Stack>

      <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>
          {confirm?.action === 'ACCEPTED'
            ? 'Accept this offer?'
            : 'Decline this offer?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.action === 'ACCEPTED'
              ? `${
                  confirm.offer.dealer
                } will receive your name, email, and phone number to finalize the ${formatUsd(
                  confirm.offer.price
                )} purchase. This closes the deal — other dealers can no longer bid.`
              : `${confirm?.offer.dealer} will be marked as declined. You can still accept another offer.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={decide}
            variant="contained"
            color={confirm?.action === 'ACCEPTED' ? 'success' : 'primary'}
            disabled={busy}
          >
            {confirm?.action === 'ACCEPTED'
              ? 'Accept & share my contact info'
              : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Deal;
